import { InfoCard } from '@/components/cards/InfoCard';
import InfoCardSkeleton from '@/components/cards/InfoCardSkeleton';
import { buildBarChartExportData } from '@/components/chart/exporters';
import StackedBarChart from '@/components/chart/StackedBarChart';
import StackedBarChartSkeleton from '@/components/chart/StackedBarChartSkeleton';
import CrumbTitle from '@/components/crumbTitle/CrumbTitle';
import {
  useLocationWiseSalesAnalytics,
  useSalesPerformance,
} from '@/features/sales-performance/hooks/useSalesPerformance';
import { useDynamicLocale } from '@/hoslog-intl';
import { ChevronLeft, ChevronRight, Payment, StoreInformation, UserFriendIcon } from '@/icons';
import { CsvIcon } from '@/icons/csv-icon';
import { MoneyBillIcon } from '@/icons/money-bill-icon';
import { WalletIcon } from '@/icons/wallet-icon';
import { exportToCSV } from '@/lib/exportToCSV';
import { createFileRoute } from '@tanstack/react-router';
import { LucideProps } from 'lucide-react';
import { ForwardRefExoticComponent, useState } from 'react';
import { Button } from 'ui/button';

export interface InfoCardType {
  icon: ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>>;
  title: string;
  info: string;
  action?: Record<string, string> & { link: string; label: string };
}

export const Route = createFileRoute('/(app)/dashboard/sales-performance')({
  loader: () => ({
    crumb: <CrumbTitle title="Sales Performance" icon={Payment} />,
    title: 'Sales Performance',
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const { salesPerformance, isLoadingSalesInfo } = useSalesPerformance();
  const { t } = useDynamicLocale();
  const [weekOffset, setWeekOffset] = useState(0);
  const { locationWiseSalesAnalytics, chartData, dateRange, isLoadingSalesAnalytics } =
    useLocationWiseSalesAnalytics(weekOffset);
  const hasSalesData = chartData.some((item) =>
    locationWiseSalesAnalytics.some((seriesItem) => Number(item[seriesItem.key]) > 0),
  );

  const salesOverviewData: InfoCardType[] = [
    {
      icon: WalletIcon,
      title: 'Total Hoslog Balance',
      info: String(salesPerformance.totalHoslogBalance?.toLocaleString() ?? 0),
    },
    {
      icon: MoneyBillIcon,
      title: 'Total Received Amount',
      info: String(salesPerformance.totalReceivedAmount?.toLocaleString() ?? 0),
      action: { label: '詳細', link: '' },
    },
    {
      icon: StoreInformation,
      title: 'Total Customer Amount',
      info: String(salesPerformance.totalCustomerAmount?.toLocaleString() ?? 0),
      action: { label: '詳細', link: '' },
    },
    {
      icon: UserFriendIcon,
      title: 'Total Affiliate Amount',
      info: String(salesPerformance.totalAffiliateAmount?.toLocaleString() ?? 0),
      action: { label: '詳細', link: '' },
    },
  ];

  return (
    <>
      {isLoadingSalesInfo ? (
        <InfoCardSkeleton />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4  gap-4">
          {salesOverviewData.map(({ icon, action, info, title }) => (
            <InfoCard key={title} icon={icon} info={info} title={title} action={action} helperText="円" />
          ))}
        </div>
      )}

      <div className="flex mt-6 gap-5.5">
        <div className="w-full bg-card py-6 px-5">
          <div className="flex items-center justify-between mb-5.5">
            <div className="text-xl font-bold">Location wise Sales</div>
            <div className="flex gap-4">
              <div className="flex items-center gap-1">
                <Button
                  aria-label="Go to previous week"
                  variant="icon"
                  size="icon"
                  className="size-4"
                  onClick={() => setWeekOffset((current) => current + 1)}
                >
                  <ChevronLeft />
                </Button>
                <span className="text-xs font-medium">{dateRange}</span>
                <Button
                  aria-label="Go to next week"
                  variant="icon"
                  size="icon"
                  className="size-4"
                  disabled={weekOffset === 0}
                  onClick={() => setWeekOffset((current) => Math.max(current - 1, 0))}
                >
                  <ChevronRight />
                </Button>
              </div>
              <Button
                variant="secondary"
                className="bg-surface-hover text-primary-950 shadow-none border-none"
                disabled={!hasSalesData}
                onClick={() => {
                  const rows = buildBarChartExportData(locationWiseSalesAnalytics, chartData);
                  exportToCSV(rows, { filename: `location-wise-sales-${dateRange.replace(/\s|~/g, '-')}` });
                }}
              >
                <CsvIcon />
                CSV形式でダウンロード
              </Button>
            </div>
          </div>

          {isLoadingSalesAnalytics ? (
            <StackedBarChartSkeleton />
          ) : hasSalesData ? (
            <StackedBarChart series={locationWiseSalesAnalytics} data={chartData} chartId="dashboardOverview" t={t} />
          ) : (
            <div className="flex min-h-[500px] items-center justify-center rounded-md border border-empty-border bg-empty-background px-6 text-center">
              <div>
                <p className="text-lg font-semibold text-empty-foreground">No sales data for this period.</p>
                <p className="mt-2 text-sm text-empty-description">
                  Please select a different date range to view sales analytics.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
